import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, Trophy } from 'lucide-react';
import { getSsgHallOfFame } from '../../api';
import { SSG_COMPETITION_NAME } from '../../ssgConstants';

const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 18px', marginBottom: 16 };
const sectionTitle = { fontSize: 13, fontWeight: 700, marginBottom: 12 };

function Avatar({ name, photo, size = 36 }) {
  if (photo) return <img src={photo} alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />;
  const initials = (name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg,#2563eb,#3b82f6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: size * 0.36, color: '#fff',
    }}>{initials}</div>
  );
}

export default function SsgHallOfFame({ onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // No season filter — "all closed periods across seasons" per this page's own spec.
    getSsgHallOfFame().then(setData).finally(() => setLoading(false));
  }, []);

  const byPoints = useMemo(() => [...(data?.summary || [])].sort((a, b) => b.all_time_points - a.all_time_points), [data]);
  const byPeriodsWon = useMemo(() => [...(data?.summary || [])].sort((a, b) => b.periods_won - a.periods_won || b.all_time_points - a.all_time_points), [data]);

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <button onClick={onBack} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12.5, marginBottom: 12, padding: 0, fontFamily: 'inherit' }}>
        <ArrowLeft size={14} /> Back to standings
      </button>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Trophy size={22} color="#f59e0b" /> {SSG_COMPETITION_NAME} — Hall of Fame
        </h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>Every closed period, every season, one place.</p>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-dimmed)' }}>Loading…</div>
      ) : (
        <>
          <div style={card}>
            <div style={sectionTitle}>Period Champions</div>
            {!data?.winners?.length ? (
              <div style={{ fontSize: 13, color: 'var(--text-dimmed)' }}>No period has been closed yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {data.winners.map((w, i) => (
                  <div key={`${w.period_id}-${w.player_id}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 10px', borderRadius: 8, background: 'var(--bg-app)', border: '1px solid var(--border-strong)' }}>
                    <span style={{ fontSize: 18 }}>🏆</span>
                    <Avatar name={w.name} photo={w.photo} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 700 }}>{w.name}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{w.season} · {w.period_name}</div>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 800 }}>{w.total_points} pts</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={card}>
              <div style={sectionTitle}>All-time points</div>
              {byPoints.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--text-dimmed)' }}>No data yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {byPoints.map((s, i) => (
                    <div key={s.player_id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                      <span style={{ width: 18, color: 'var(--text-dimmed)', fontWeight: 700 }}>{i + 1}</span>
                      <Avatar name={s.name} photo={s.photo} size={28} />
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                      <span style={{ fontWeight: 700 }}>{s.all_time_points}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={card}>
              <div style={sectionTitle}>Most periods won</div>
              {byPeriodsWon.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--text-dimmed)' }}>No data yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {byPeriodsWon.map((s, i) => (
                    <div key={s.player_id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                      <span style={{ width: 18, color: 'var(--text-dimmed)', fontWeight: 700 }}>{i + 1}</span>
                      <Avatar name={s.name} photo={s.photo} size={28} />
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                      <span style={{ fontWeight: 700 }}>{s.periods_won}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
